import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getEvents } from '../../api/raceManager'
import { format } from 'date-fns'
import { useTheme } from '../../context/ThemeContext'
import { useAuth } from '../../context/AuthContext'
import { useTranslation } from 'react-i18next'
import UserMenu from '../auth/UserMenu'

export default function Header() {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const { id } = useParams()
  const { theme, toggleTheme } = useTheme()
  const { updatePreference, preferences } = useAuth()
  const { t, i18n } = useTranslation()

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await getEvents(1)
        setEvents(data.results || [])
      } catch (error) {
        console.error('Failed to fetch events:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchEvents()
  }, [])

  const handleEventChange = e => {
    const eventId = e.target.value
    if (eventId) {
      navigate(`/events/${eventId}/groups`)
    }
  }

  const selectedEvent = events.find(e => e.id === parseInt(id))

  const handleLanguageChange = (lang) => {
    updatePreference('language', lang)
  }

  return (
    <header className="bg-surface-elevated border-b border-subtle">
      <div
        className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 py-3 md:py-4
        flex justify-between items-center gap-2"
      >
        {/* Logo - visible on all screens */}
        <div
          className="flex items-center cursor-pointer flex-shrink-0"
          onClick={() => navigate('/events')}
        >
          <img
            src="/logo.png"
            alt="kompas.racing"
            className="h-10 md:h-12 lg:h-14 w-auto"
          />
          <h1
            className="hidden lg:inline ml-3 text-spectator-lg
            font-bold text-primary"
          >
            kompas.racing
          </h1>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          {/* Language Switcher */}
          <select
            value={i18n.language}
            onChange={(e) => handleLanguageChange(e.target.value)}
            className="bg-surface text-primary-content border
              border-moderate rounded-lg px-2 py-1
              text-sm cursor-pointer
              hover:border-primary transition-colors
              focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Select language"
          >
            <option value="en">EN</option>
            <option value="pl">PL</option>
          </select>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700
              hover:bg-gray-300 dark:hover:bg-gray-600
              transition-colors"
            aria-label="Toggle theme"
            title={t('common.switchTheme', `Switch to ${theme === 'light' ? 'dark' : 'light'} mode`)}
          >
            {theme === 'light' ? (
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20.354 15.354A9 9 0 018.646 3.646
                    9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                />
              </svg>
            ) : (
              <svg
                className="w-6 h-6 text-yellow-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364
                    6.364l-.707-.707M6.343 6.343l-.707-.707m12.728
                    0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0
                    11-8 0 4 4 0 018 0z"
                />
              </svg>
            )}
          </button>

          {/* Event Selector */}
          <select
            value={id || ''}
            onChange={handleEventChange}
            disabled={loading}
            className="bg-surface text-primary-content border
              border-moderate rounded-lg px-2 md:px-4 py-2
              text-sm md:text-base cursor-pointer
              hover:border-primary transition-colors
              focus:outline-none focus:ring-2 focus:ring-primary
              disabled:opacity-50 max-w-[140px] sm:max-w-xs"
            aria-label="Select event"
          >
            <option value="">{t('common.selectEvent', 'Select Event')}</option>
            {events.map(event => (
              <option key={event.id} value={event.id}>
                {event.name} - {event.location} (
                {format(new Date(event.date), 'MMM d, yyyy')})
              </option>
            ))}
          </select>

          <UserMenu />
        </div>
      </div>
    </header>
  )
}
